import { mapActions, mapGetters, mapMutations } from 'vuex';
import { filter, tap } from 'rxjs/operators';

import { AuthService } from '@/services';

import {
    getLocalStorage,
    getParameterByName,
    indexById,
    isValueExist,
    removeAdrielTokens,
} from '@/utils/utils';
import {
    handleNoWorkspaceAccess as handleNoWorkspaceAccessFn,
    redirectAfterJoin as redirectAfterJoinFn,
} from '@/utils/authRedirect';
import {
    LAST_ACCOUNT_ID_STORAGE_KEY,
    clearStorageOnLogout,
    genLegacyLastAccountIdKey,
    migrateLocalStorageKey,
} from '@/utils/logoutStorage';
import { NOTI_TYPES } from '@/store/dashboardEdit';
import { ACCOUNT_ROLES_NEW } from '@/utils/consts';

export default {
    computed: {
        ...mapGetters('user', ['role', 'accounts', 'user']),
    },
    methods: {
        ...mapActions('user', ['logout', 'fetchAccountList', 'fetchWorkspace']),
        ...mapMutations('user', ['setAccountId']),
        isInviteFlow() {
            return !!getParameterByName('invite_token');
        },
        isWhitelabelUrl() {
            const adrielDomainRegex = /(.*)(adriel\.com)$/;
            const hostname = window.location.hostname;
            return (
                hostname !== 'localhost' && !adrielDomainRegex.test(hostname)
            );
        },
        handleNoWorkspaceAccess() {
            return handleNoWorkspaceAccessFn({
                router: this.$router,
                route: this.$route,
                accounts: this.accounts,
            });
        },
        resendEmail(email) {
            AuthService.resendEmail({
                email,
            }).then(() =>
                this.addNotiMsg({
                    type: NOTI_TYPES.SUCCESS,
                    title: this.$t('LOGIN.verify_sent_again'),
                })
            );
        },
        fetchWorkspaceById: (() => {
            let lastId;

            return function (wsId) {
                if (lastId == wsId) {
                    return;
                }

                lastId = wsId;

                this.setAccountId(wsId);

                return this.fetchWorkspace().then((data = {}) => {
                    const type = data.account_type;

                    const updatedQuery = R.compose(
                        R.propOr({}, 'query'),
                        R.when(
                            R.propEq('name', 'Connection'),
                            R.compose(
                                R.assocPath(['query', 'planType'], type),
                                R.dissocPath(['query', 'displayConnection'])
                            )
                        )
                    )(this.$route);

                    type &&
                        this.$router.replace({
                            query: updatedQuery,
                        });
                });
            };
        })(),
        fetchWorkspaceList(userId) {
            return this.fetchAccountList().then((res) => {
                if (!isValueExist(res)) {
                    // Don't auto-redirect if on verification flow pages
                    const shouldNotRedirect = [
                        'Verify',
                        'EmptyWs',
                        'Result',
                    ].includes(this.$route.name);

                    if (!shouldNotRedirect) {
                        return this.handleNoWorkspaceAccess();
                    }
                    // If on verify flow, do nothing - let user click OK button
                    return;
                }

                migrateLocalStorageKey(
                    genLegacyLastAccountIdKey(userId),
                    LAST_ACCOUNT_ID_STORAGE_KEY
                );

                // URL, then the device's own memory, then the server's
                // (users.meta). The device copy comes first so this agrees with
                // the reload path in App.vue, which only has the device copy:
                // otherwise a reload could open one workspace and then switch to
                // another. The server copy is what a browser with nothing stored
                // falls back to. A candidate only counts when it is one of this
                // user's workspaces (checked against `res`), else the default.
                const candidates = [
                    getParameterByName('accountId'),
                    getLocalStorage(LAST_ACCOUNT_ID_STORAGE_KEY),
                    R.path(['meta', 'lastWorkspaceId'], this.user),
                ].filter(R.identity);

                const candidateId = R.compose(
                    R.path([0, 'id']),
                    R.filter(isValueExist),
                    R.map(R.prop(R.__, indexById(res)))
                )(candidates);

                const defaultAccount =
                    R.find(R.prop('is_default'))(res) || R.head(res);
                const _accountId = candidateId || defaultAccount.id;

                return this.fetchWorkspaceById(_accountId);
            });
        },
        redirectAfterJoin() {
            return redirectAfterJoinFn({
                router: this.$router,
                route: this.$route,
                accounts: this.accounts,
                isMobileSize: this.isMobileSize,
                isPlanT: this.isPlanT,
            });
        },
        async logoutUser(onReload) {
            removeAdrielTokens();
            clearStorageOnLogout();

            const promise = this.logout();

            const reload = () => {
                promise.then(() => this.$router.go());

                onReload && onReload();
            };

            this.$router.push(
                {
                    name: 'Sign/Login',
                    query: R.pick(['planType'], this.$route.query),
                },
                reload,
                reload
            );
        },
    },
};
